import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getSubmission, updateSubmission, reviewSubmission, clearCurrentSubmission } from '../actions/submissionActions';
import SubmissionForm from '../components/content-management/SubmissionForm';
import AdminLayout from '../components/settings/AdminLayout';

import { isAdminRole } from '../utils/roleHelpers';

const SubmissionEditor = ({
  auth: { user },
  submission: { currentSubmission, loading: submissionLoading },
  getSubmission,
  updateSubmission,
  reviewSubmission,
  clearCurrentSubmission
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showForm, setShowForm] = useState(false);

  // Determine mode from URL path
  const isView = location.pathname.includes('/view');
  const isEdit = location.pathname.includes('/edit');
  const mode = isView ? 'view' : 'edit';

  // Role detection
  const isAdmin = isAdminRole(user?.role);
  const isInstructor = user?.role === 'instructor';

  useEffect(() => {
    // Load submission data if viewing or editing
    if (id) {
      getSubmission(id);
    }

    // Show form after short delay to ensure data is loaded
    const timer = setTimeout(() => {
      setShowForm(true);
    }, 100);

    // Cleanup: clear current submission when component unmounts
    return () => {
      clearTimeout(timer);
      clearCurrentSubmission();
    };
  }, [id, getSubmission, clearCurrentSubmission]);

  const handleClose = () => {
    navigate('/settings', { state: { activeTab: 'Submissions' } });
  };

  const handleSubmit = async (submissionData) => {
    try {
      if (isEdit && id) {
        // Use reviewSubmission for status updates, updateSubmission for other changes
        if (submissionData.status && submissionData.status !== currentSubmission?.status) {
          await reviewSubmission(id, submissionData);
        } else {
          await updateSubmission(id, submissionData);
        }
      }
      // Navigate back to submissions list after successful save
      navigate('/settings', { state: { activeTab: 'Submissions' } });
    } catch (error) {
      console.error('Error submitting submission:', error);
      throw error;
    }
  };

  // Show loading spinner while data is being fetched
  if (submissionLoading) {
    return (
      <AdminLayout activeTab="Submissions">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1086a4]"></div>
          <span className="ml-4 text-gray-600">Loading submission...</span>
        </div>
      </AdminLayout>
    );
  }

  // Show form only after data is ready
  if (!showForm) {
    return (
      <AdminLayout activeTab="Submissions">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1086a4]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="Submissions">
      <SubmissionForm
        mode={mode}
        submission={currentSubmission}
        onClose={handleClose}
        onSubmit={handleSubmit}
        inline={true}
      />
    </AdminLayout>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  submission: state.submission
});

export default connect(
  mapStateToProps,
  {
    getSubmission,
    updateSubmission,
    reviewSubmission,
    clearCurrentSubmission
  }
)(SubmissionEditor);
